import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { loadingContent } from '../../components/EmptyStates.jsx';
import { navigateToChangeImpactReports } from '../../components/links/index.jsx';
import {
  retrieveOrganizationImpactedEntities,
  searchOrganizationChangeImpacts,
  selectImpactedEntities,
  selectRetrieveImpactedEntitiesState,
  selectSearchChangeImpactsState,
  selectSearchResults,
} from '../../modules/changeImpacts.js';
import ChangeImpactsOverview from '../../components/reports/ChangeImpactReports/ChangeImpactsOverview/ChangeImpactsOverview.jsx';
import {
  selectCurrentOrganizationId,
  selectUserPermissions,
} from '../../modules/auth.js';
import { selectOrganization } from '../../modules/organizations.js';
import ChangeImpactTable from '../../components/reports/ChangeImpactReports/ChangeImpactTable.jsx';
import {
  retrieveChangeImpactTypes,
  selectChangeImpactTypesWithArchived,
} from '../../modules/changeImpactTypes.js';
import {
  retrievePortfolios,
  selectPortfolios,
} from '../../modules/portfolio.js';
import {
  retrieveProjects,
  selectProjects,
  selectRetrieveProjectsState,
} from '../../modules/projects.js';
import ReportsHeader from '../../components/headers/PageHeaders/Reports/ReportsHeader.jsx';
import Empty from '../../components/Empty.jsx';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import SubHeader from '../../components/SubHeader.jsx';
import ToggleBlock from '../../components/ToggleBlock.jsx';
import CalendarHeatmap from '../../components/reports/ChangeImpactReports/ChangeImpactHeatMap/index.jsx';
import ReportTypeSelect from '../../components/reports/ChangeImpactReports/ReportTypeSelect.jsx';
import ChangeImpactActionDropdown from '../../components/reports/ChangeImpactReports/ChangeImpactActionDropdown.jsx';
import ReportFilters from '../../components/reports/ChangeImpactReports/ReportFilters/index.jsx';
import { sortBy, sortQueryParams, sortSearchResults } from './utils/index.js';
import SectionHeaderWithSort from '../../components/SectionHeaderWithSort.jsx';
import { resetQuery } from '../../components/reports/ChangeImpactReports/utils/index.js';

const mapStateToProps = (state) => {
  const projects = selectProjects(state);
  const organizationId = selectCurrentOrganizationId(state);
  const impactedEntities = selectImpactedEntities(organizationId)(state);

  const retrieveSuccess =
    selectRetrieveImpactedEntitiesState(state).success &&
    selectRetrieveProjectsState(state).success;

  const { query, error, pending, success } =
    selectSearchChangeImpactsState(state);

  return {
    retrieveSuccess,
    projects,
    organizationId,
    impactedEntities,
    searchQuery: query,
    searchPending: pending,
    searchSuccess: success,
    searchError: error,
    portfolios: selectPortfolios(state),
    searchResults: selectSearchResults(state),
    changeImpactTypes: selectChangeImpactTypesWithArchived(state),
    apiUrl: state.config.apiUrl,
    permissions: selectUserPermissions(state),
    organization: selectOrganization(state),
  };
};

const mapDispatchToProps = {
  retrieveOrganizationImpactedEntities,
  searchOrganizationChangeImpacts,
  retrieveProjects,
  retrieveChangeImpactTypes,
  retrievePortfolios,
};

function ChangeImpactReportsContainer(props) {
  const {
    slug,
    organizationId,
    projects,
    portfolios,
    impactedEntities,
    changeImpactTypes,
    retrieveOrganizationImpactedEntities,
    retrieveChangeImpactTypes,
    retrievePortfolios,
    searchOrganizationChangeImpacts,
    retrieveProjects,
    retrieveSuccess,
    searchPending,
    query,
    searchResults,
    permissions,
    apiUrl,
    organization,
  } = props;

  const { loading, downloadBlob } = useFetchFile();
  const [viewType, setViewType] = useState('pie');
  const [sortType, setSortType] = useState(sortBy[0]);
  const pieTypeRef = useRef(null);
  const piePerceptionRef = useRef(null);
  const pieScaleRef = useRef(null);

  const handleSelect = (e) => {
    if (e === 'heat') {
      const currentDate = new Date();

      const currentYear = currentDate.getFullYear();
      const startOfCurrentYear = new Date(currentYear, 0, 1);

      const nextYear = currentYear + 1;
      const endOfNextYear = new Date(nextYear, 11, 31);
      navigateToChangeImpactReports({
        ...resetQuery,
        startDate: moment(startOfCurrentYear).format('YYYY-MM-DD'),
        endDate: moment(endOfNextYear).format('YYYY-MM-DD'),
      });
      setViewType(e);
    } else {
      navigateToChangeImpactReports({
        ...resetQuery,
        startDate: null,
        endDate: null,
      });
      setViewType(e);
    }
  };

  const onDownloadReport = () => {
    const body =
      viewType === 'pie'
        ? JSON.stringify({
            type_chart_html: pieTypeRef.current.outerHTML,
            perception_chart_html: piePerceptionRef.current.outerHTML,
            scale_chart_html: pieScaleRef.current.outerHTML,
          })
        : null;

    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/change_impacts/pdf`,
      { ...query, ...sortQueryParams[sortType] },
      'change-impacts-report',
      'pdf',
      'POST',
      body
    );
  };

  useEffect(() => {
    retrieveProjects();
    retrieveChangeImpactTypes();
    retrievePortfolios();
    retrieveOrganizationImpactedEntities(organizationId);
  }, []);

  useEffect(() => {
    searchOrganizationChangeImpacts(organizationId, query);
  }, [query]);

  if (!retrieveSuccess || !projects || !impactedEntities) {
    return <Empty simplified content={loadingContent} />;
  }

  return (
    <React.Fragment>
      <ReportsHeader slug={slug} />
      <SubHeader flexJustify small>
        <h2>Change Impacts Reports</h2>
        <div className="flex">
          <ReportTypeSelect reportType={viewType} onSelect={handleSelect} />
          {viewType !== 'heat' ? (
            <ChangeImpactActionDropdown
              viewType={viewType}
              isReportLoading={loading}
              onDownloadReport={onDownloadReport}
            />
          ) : null}
        </div>
      </SubHeader>

      <ToggleBlock title="Filters" showOnStart>
        <ReportFilters
          query={query}
          organizationId={organizationId}
          resetDependencies={[viewType]}
          permissions={permissions}
          hasParentPortfolios={organization && organization.hasParentPortfolios}
        />
      </ToggleBlock>

      {viewType === 'heat' ? (
        <CalendarHeatmap slug={slug} results={searchResults} query={query} />
      ) : (
        <ChangeImpactsOverview
          pieTypeRef={pieTypeRef}
          piePerceptionRef={piePerceptionRef}
          pieScaleRef={pieScaleRef}
          results={searchResults}
          changeImpactTypes={changeImpactTypes}
        />
      )}

      <div
        className="heatmap-table"
        style={{ opacity: searchPending ? 0.5 : 1 }}
      >
        {searchResults.length ? (
          <React.Fragment>
            <SectionHeaderWithSort
              title="Change Impacts Reports Table"
              total={searchResults.length}
              sortOptions={sortBy}
              defaultSort={sortType}
              onSortChange={setSortType}
            />
            <ChangeImpactTable
              changeImpacts={sortSearchResults(searchResults, sortType)}
              portfolios={portfolios}
              projects={projects}
              slug={slug}
            />
          </React.Fragment>
        ) : null}
      </div>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeImpactReportsContainer);
